/**
 * Creates a branded type to distinguish primitives at compile-time.
 * The brand exists only in TypeScript's type system and has no runtime cost.
 */
export type Brand<BaseType, BrandName extends string> = BaseType & {
  readonly __brand: BrandName;
};
export type LegacyCompanyId = Brand<number, 'LegacyCompanyId'>;
export type LegacyPersonId = Brand<number, 'LegacyPersonId'>;
export type LegacyAssigneeUserId = Brand<number, 'LegacyAssigneeUserId'>;

export type CompanyId = Brand<string, 'CompanyId'>;
export type PersonId = Brand<string, 'PersonId'>;
export type AssigneeUserId = Brand<string, 'AssigneeUserId'>;

export type MigrationCompanyId = LegacyCompanyId | CompanyId;
export type MigrationPersonId = LegacyPersonId | PersonId;
export type MigrationAssigneeUserId = LegacyAssigneeUserId | AssigneeUserId;
type RawIdInput = string | number | null | undefined;

/**
 * Creates a branded CompanyId from a validated string.
 * Throws if input is invalid. Use toCompanyId() for safe conversion.
 * */
export const asCompanyId = (value: string): CompanyId => {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Invalid CompanyId: expected non-empty string, got: "${value}"`);
  }
  if (trimmed.length > 100) {
    throw new Error(`Invalid CompanyId: exceeds max length of 100 characters`);
  }
  return trimmed as CompanyId;
};

/**
 * Creates a branded LegacyCompanyId from a validated number.
 * Throws if input is invalid.
 */
export const asLegacyCompanyId = (value: number): LegacyCompanyId => {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid LegacyCompanyId: expected finite number, got: ${value}`);
  }
  if (value < 0) {
    throw new Error(`Invalid LegacyCompanyId: expected positive number, got: ${value}`);
  }
  return value as LegacyCompanyId;
};

/**
 * Creates a branded PersonId from a validated string.
 * Throws if input is invalid.
 */
const asPersonId = (value: string): PersonId => {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Invalid PersonId: expected non-empty string, got: "${value}"`);
  }
  if (trimmed.length > 100) {
    throw new Error(`Invalid PersonId: exceeds max length of 100 characters`);
  }
  return trimmed as PersonId;
};

/**
 * Creates a branded LegacyPersonId from a validated number.
 * Throws if input is invalid.
 */
const asLegacyPersonId = (value: number): LegacyPersonId => {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid LegacyPersonId: expected finite number, got: ${value}`);
  }
  if (value < 0) {
    throw new Error(`Invalid LegacyPersonId: expected positive number, got: ${value}`);
  }
  return value as LegacyPersonId;
};

/**
 * Creates a branded AssigneeUserId from a validated string.
 */
export const asAssigneeUserId = (value: string): AssigneeUserId => {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Invalid AssigneeUserId: expected non-empty string, got: "${value}"`);
  }
  if (trimmed.length > 100) {
    throw new Error(`Invalid AssigneeUserId: exceeds max length of 100 characters`);
  }
  return trimmed as AssigneeUserId;
};

/**
 * Creates a branded LegacyAssigneeUserId from a validated number.
 * Throws if input is invalid.
 */
const asLegacyAssigneeUserId = (value: number): LegacyAssigneeUserId => {
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid LegacyAssigneeUserId: expected finite number, got: ${value}`);
  }
  if (value < 0) {
    throw new Error(`Invalid LegacyAssigneeUserId: expected positive number, got: ${value}`);
  }
  return value as LegacyAssigneeUserId;
};

/**
 * Safely converts raw input to MigrationCompanyId (branded type).
 */
export const toCompanyId = (input: RawIdInput): MigrationCompanyId | null => {
  if (input == null) {
    return null;
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) {
      return null;
    }
    try {
      return asLegacyCompanyId(input);
    } catch {
      return null;
    }
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > 100) {
      return null;
    }
    try {
      return asCompanyId(trimmed);
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Safely converts raw input to MigrationPersonId.
 * @see toCompanyId for usage examples
 */
export const toPersonId = (input: RawIdInput): MigrationPersonId | null => {
  if (input == null) {
    return null;
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) {
      return null;
    }
    try {
      return asLegacyPersonId(input);
    } catch {
      return null;
    }
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > 100) {
      return null;
    }
    try {
      return asPersonId(trimmed);
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Safely converts raw input to MigrationAssigneeUserId.
 * @see toCompanyId for usage examples
 */
export const toAssigneeUserId = (input: RawIdInput): MigrationAssigneeUserId | null => {
  if (input == null) {
    return null;
  }

  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) {
      return null;
    }
    try {
      return asLegacyAssigneeUserId(input);
    } catch {
      return null;
    }
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > 100) {
      return null;
    }
    try {
      return asAssigneeUserId(trimmed);
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Normalizes any ID to canonical string format.
 */
export const normalizeToString = (
  id: MigrationCompanyId | MigrationPersonId | MigrationAssigneeUserId | null | undefined
): string | null => {
  if (id == null) {
    return null;
  }

  // Handle both number and string branded types
  const str = String(id).trim();

  if (str.length === 0) {
    return null;
  }

  // Sanity check: max length
  if (str.length > 100) {
    return null;
  }

  return str;
};

/**
 * Type guard to check if a value is a valid ID.
 * Useful for filtering arrays or validating user input.
 */
export const isValidId = (value: unknown): value is MigrationCompanyId | MigrationPersonId | MigrationAssigneeUserId => {
  if (value == null) {
    return false;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed.length <= 100;
  }

  return false;
};
